// src/components/site-editor/panels/SocialLinksPanel.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import { socialPlatforms } from '../../../data/socialPlatforms';
import ToggleGroup from '../../common/ToggleGroup';
import { auth, db, isFirebaseInitialized } from '../../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { OAUTH_PLATFORMS } from '../../../services/social-auth-service';

const SocialLinksPanel = () => {
  const { state, actions } = useWebsiteBuilder();
  const { draft } = state;

  const [socialOAuth, setSocialOAuth] = useState({});

  useEffect(() => {
    if (!isFirebaseInitialized()) return;

    let unsubFirestore = null;
    let done = false;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (done) return;
      if (user) {
        const practRef = doc(db, 'practitioners', user.uid);
        unsubFirestore = onSnapshot(
          practRef,
          (snap) => setSocialOAuth(snap.data()?.socialConnections || {}),
          (err) => console.warn('SocialLinksPanel: Firestore error:', err)
        );
      } else {
        setSocialOAuth({});
      }
    });

    const timeout = setTimeout(() => { done = true; }, 3000);
    return () => {
      done = true;
      unsubAuth();
      clearTimeout(timeout);
      if (unsubFirestore) unsubFirestore();
    };
  }, []);

  // BRIDGE: Temporarily syncs OAuth data to draft fields the WebsiteRenderer reads.
  // Will be removed in Step 7 when IntegrationsSettings.js takes over this responsibility.
  useEffect(() => {
    if (!actions || !Object.keys(socialOAuth).length) return;
    const currentLinks = draft.contactFooter?.socialLinks || {};
    const updatedLinks = { ...currentLinks };
    let changed = false;
    OAUTH_PLATFORMS.forEach((platformId) => {
      const conn = socialOAuth[platformId];
      if (conn?.connected && conn?.profileUrl && !currentLinks[platformId]) {
        updatedLinks[platformId] = conn.profileUrl;
        changed = true;
      }
    });
    if (changed) {
      actions.updateContactFooter('socialLinks', updatedLinks);
    }
  }, [socialOAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  // BRIDGE: Temporarily syncs OAuth data to draft fields the WebsiteRenderer reads.
  // Will be removed in Step 7 when IntegrationsSettings.js takes over this responsibility.
  useEffect(() => {
    if (!actions || !Object.keys(socialOAuth).length) return;
    const current = draft.contactFooter?.socialVerified || {};
    const updated = { ...current };
    let changed = false;
    OAUTH_PLATFORMS.forEach((platformId) => {
      if (socialOAuth[platformId]?.connected === true && !current[platformId]) {
        updated[platformId] = true;
        changed = true;
      }
    });
    if (changed) {
      actions.updateContactFooter('socialVerified', updated);
    }
  }, [socialOAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIconStyleChange = (style) => actions.updateContactFooter('socialIconStyle', style);
  const handleIconShapeChange = (shape) => actions.updateContactFooter('socialIconShape', shape);
  const handleDisplayLocationChange = (loc) => actions.updateContactFooter('socialDisplayLocation', loc);

  const connectedPlatforms = socialPlatforms.filter((p) => {
    const oauthConnected = socialOAuth[p.id]?.connected === true;
    const manualLink = draft.contactFooter?.socialLinks?.[p.id];
    return oauthConnected || (manualLink && manualLink.length > 0);
  });

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Social Media Links</h3>
        <p className="text-sm text-slate-600">Customize how your social links appear on your site</p>
      </div>

      <div className="pb-4 border-b border-slate-200">
        <Link
          to="/dashboard/settings/integrations"
          className="text-sm font-medium hover:opacity-80 transition-opacity"
          style={{ color: '#bb9749' }}
        >
          Manage social accounts in Integrations →
        </Link>
      </div>

      <div>
        <p className="text-sm font-medium text-slate-700 mb-2">Connected accounts</p>
        {connectedPlatforms.length > 0 ? (
          <div className="space-y-2">
            {connectedPlatforms.map((platform) => {
              const oauthVerified = OAUTH_PLATFORMS.includes(platform.id) && socialOAuth[platform.id]?.connected === true;
              return (
                <div key={platform.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <span className="flex items-center justify-center w-5 h-5 flex-shrink-0" style={{ color: platform.color }}>
                    {platform.icon}
                  </span>
                  <span className="text-sm font-medium text-slate-900">{platform.name}</span>
                  {oauthVerified && (
                    <span className="ml-auto text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                      Verified
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">No social accounts connected yet.</p>
        )}
      </div>

      <div className="pt-4 border-t border-slate-200">
        <label className="block text-sm font-medium text-slate-700 mb-3">Icon Style</label>
        <ToggleGroup
          options={[
            { value: 'filled', label: 'Filled' },
            { value: 'outlined', label: 'Outlined' },
            { value: 'minimal', label: 'Minimal' }
          ]}
          value={draft.contactFooter.socialIconStyle}
          onChange={handleIconStyleChange}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-3">Icon Shape</label>
        <div style={{ opacity: draft.contactFooter.socialIconStyle === 'minimal' ? 0.5 : 1 }}>
          <ToggleGroup
            options={[
              { value: 'circle', label: 'Circle' },
              { value: 'rounded', label: 'Rounded' },
              { value: 'square', label: 'Square' }
            ]}
            value={draft.contactFooter.socialIconShape || 'circle'}
            onChange={handleIconShapeChange}
          />
        </div>
        {draft.contactFooter.socialIconStyle === 'minimal' && (
          <p className="text-xs text-gray-500 mt-2">Shape doesn't apply to Minimal style</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-3">Display Location</label>
        <ToggleGroup
          options={[
            { value: 'header', label: 'Header' },
            { value: 'footer', label: 'Footer' },
            { value: 'both', label: 'Both' }
          ]}
          value={draft.contactFooter.socialDisplayLocation}
          onChange={handleDisplayLocationChange}
        />
      </div>

      <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-100">
        <h4 className="text-sm font-semibold text-green-900 mb-2">Privacy Tip</h4>
        <p className="text-xs text-green-800">
          Only add platforms where you're comfortable with clients reaching out.
          Leave others blank if you prefer not to share them.
        </p>
      </div>
    </div>
  );
};

export default SocialLinksPanel;
